import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaTimes } from 'react-icons/fa';
import { toEditFormDates, toEditedDatesPayload } from '../../../utils/dateOnly';
import Modal, { Field } from '../../common/Modal';
import { employeesApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';

const EditRoleModal = ({
  isOpen,
  onClose,
  employeeId,
  roleEntry,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null
  });

  useEffect(() => {
    if (isOpen && roleEntry) {
      // ADR-197: pre-fill the stored calendar day, not its local-instant reading
      setFormData(toEditFormDates(roleEntry));
    }
  }, [isOpen, roleEntry]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const payload = toEditedDatesPayload(formData, roleEntry);

      await employeesApi.updateRoleHistory(employeeId, roleEntry.id, payload);
      showAlert('Role history updated successfully', 'success');
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error updating role history:', error);
      showAlert(error.message || 'Failed to update role history', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ startDate: null, endDate: null });
      onClose();
    }
  };

  const title = roleEntry?.role?.label ? `Edit Role: ${roleEntry.role.label}` : 'Edit Role';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Saving...' : 'Save Changes'}
      submitDisabled={!formData.startDate || isSubmitting}
      width="450px"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
        <Field>
          <DatePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, startDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, startDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </Field>

        {/* Only show end date field for historical roles (those with an existing end date) */}
        {roleEntry?.endDate && (
          <Field>
            <DatePicker
              label="End Date"
              value={formData.endDate}
              onChange={(newValue) => {
                if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                  setFormData(prev => ({ ...prev, endDate: newValue }));
                } else if (newValue === null) {
                  setFormData(prev => ({ ...prev, endDate: null }));
                }
              }}
              format="DD/MM/YYYY"
              disabled={isSubmitting}
              slotProps={{
                textField: {
                  fullWidth: true,
                  inputProps: { placeholder: 'DD/MM/YYYY' }
                }
              }}
            />
            {formData.endDate && (
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                onClick={() => setFormData(prev => ({ ...prev, endDate: null }))}
              >
                <FaTimes size={12} /> Clear date
              </Typography>
            )}
          </Field>
        )}
      </Box>
    </Modal>
  );
};

export default EditRoleModal;
